import type { PlanScene, ScenePrimitive, StyleToken } from "../geometry/scene";
import type { Point } from "../geometry/model";
import { PU_PER_FOOT, PU_PER_INCH, formatLength } from "../geometry/units";

/**
 * PRINTABLE DESIGN SHEETS (spec §60M).
 *
 * A floor plan on paper, drawn as VECTORS — never a screenshot of the
 * canvas. The plan is emitted as an SVG whose viewBox is in plan units and
 * whose physical width and height are stated in INCHES, so the browser's own
 * print pipeline produces a PDF that is genuinely to scale: at 1/4" = 1'-0"
 * a forty-foot wall measures ten inches on the paper, and a ruler agrees.
 *
 * The one rule that matters: **a drawing is only ever labelled with a scale
 * it actually has.** If the plan does not fit the sheet at the requested
 * scale, either a smaller standard scale is chosen and named, or the drawing
 * is fitted to the sheet and labelled NOT TO SCALE. It is never labelled
 * 1/4" = 1'-0" and then quietly shrunk to fit.
 *
 * Pure: no DOM, no React, no database. It takes a PlanScene (which the
 * geometry engine already produces for the editor) and returns a string.
 */

export interface SheetSize {
  id: string;
  label: string;
  /** Paper size in inches, portrait. Plans print landscape by default. */
  widthIn: number;
  heightIn: number;
}

export const SHEET_SIZES: readonly SheetSize[] = [
  { id: "letter", label: "Letter (8.5 × 11)", widthIn: 8.5, heightIn: 11 },
  { id: "legal", label: "Legal (8.5 × 14)", widthIn: 8.5, heightIn: 14 },
  { id: "tabloid", label: "Tabloid (11 × 17)", widthIn: 11, heightIn: 17 },
  { id: "arch-c", label: "Arch C (18 × 24)", widthIn: 18, heightIn: 24 },
  { id: "arch-d", label: "Arch D (24 × 36)", widthIn: 24, heightIn: 36 },
];

export interface ArchitecturalScale {
  id: string;
  /** How a drawing set writes it. */
  label: string;
  /** Paper inches per real-world foot. */
  paperInchesPerFoot: number;
}

/** The ladder a residential plan is normally drawn at, largest first. */
export const ARCHITECTURAL_SCALES: readonly ArchitecturalScale[] = [
  { id: "half", label: '1/2" = 1\'-0"', paperInchesPerFoot: 0.5 },
  { id: "quarter", label: '1/4" = 1\'-0"', paperInchesPerFoot: 0.25 },
  { id: "three-sixteenths", label: '3/16" = 1\'-0"', paperInchesPerFoot: 0.1875 },
  { id: "eighth", label: '1/8" = 1\'-0"', paperInchesPerFoot: 0.125 },
  { id: "three-thirty-seconds", label: '3/32" = 1\'-0"', paperInchesPerFoot: 0.09375 },
  { id: "sixteenth", label: '1/16" = 1\'-0"', paperInchesPerFoot: 0.0625 },
];

export interface SheetMargins {
  /** Printable margin on every edge, inches. */
  edgeIn: number;
  /** Height reserved for the title block along the bottom, inches. */
  titleBlockIn: number;
}

export const DEFAULT_MARGINS: SheetMargins = { edgeIn: 0.5, titleBlockIn: 1.1 };

export interface DesignSheetInfo {
  projectName: string;
  address: string;
  jobNumber: string;
  designName: string;
  designKind: "existing" | "proposed";
  floorName: string;
  /** ISO date the sheet was produced. */
  generatedAt: string;
  sheetTitle: string;
  sheetNumber: string;
}

export interface DesignSheetOptions {
  size: SheetSize;
  /** A specific scale, or 'fit' to fill the sheet and say so. */
  scale: ArchitecturalScale | "fit";
  orientation: "landscape" | "portrait";
  margins?: SheetMargins;
  /**
   * ANNOTATION SIZE (spec §70M): a paper-space multiplier for text ONLY.
   * Geometry follows the drawing scale; annotations stay legible on
   * paper — two related but deliberately separate systems.
   * 1 = the compact base sizes; the registry maps Small/Standard/Large.
   */
  annotationScale?: number;
}

/** What Small / Standard / Large mean, in one place (spec §70C). */
export const ANNOTATION_SCALES: Record<string, number> = {
  small: 1,
  standard: 1.35,
  large: 1.75,
};

/** Bound title-block wording at its printed font size; full identity stays in the report header. */
export function fitSheetTitle(value:string,widthIn:number,sizePt:number):string {
  const units=(text:string)=>[...text].reduce((n,c)=>n+(/[MW@%]/.test(c)?.95:/[ilI.,' :!|]/.test(c)?.3:/[A-Z0-9]/.test(c)?.72:c.charCodeAt(0)>255?1:.6),0);
  const capacity=Math.max(0,widthIn*72/sizePt/1.12);
  if(units(value)<=capacity)return value;
  let out="";for(const c of value){if(units(out+c+"...")>capacity)break;out+=c;}
  return out.trimEnd()+"...";
}

export interface DesignSheetLayout {
  /** Paper size actually used, in inches, after orientation. */
  paper: { widthIn: number; heightIn: number };
  /** The drawing area inside the margins and above the title block. */
  drawing: { widthIn: number; heightIn: number };
  /** World bounds the drawing covers, in plan units. */
  bounds: { min: Point; max: Point };
  /** The scale finally used, or null when the drawing was fitted. */
  scale: ArchitecturalScale | null;
  /** What the sheet says about its scale. Always true. */
  scaleLabel: string;
  /** Physical size of the plan on the paper, inches. */
  plan: { widthIn: number; heightIn: number };
  /** True when a requested scale did not fit and a smaller one was chosen. */
  scaleReduced: boolean;
}

/** Paper inches a length of `pu` occupies at a given scale. */
export function paperInchesFor(pu: number, scale: ArchitecturalScale): number {
  const feet = pu / PU_PER_FOOT;
  return feet * scale.paperInchesPerFoot;
}

function boundsOf(scene: PlanScene): { min: Point; max: Point } {
  if (scene.bounds) return scene.bounds;
  return { min: { x: 0, y: 0 }, max: { x: PU_PER_FOOT * 20, y: PU_PER_FOOT * 20 } };
}

/**
 * Work out how the plan sits on the paper.
 *
 * A requested scale is honoured when the plan fits. When it does not, the
 * next scale down the standard ladder is tried, and the sheet says which one
 * was used — a drawing that claims a scale it does not have is worse than no
 * scale at all.
 */
export function layoutDesignSheet(scene: PlanScene, options: DesignSheetOptions): DesignSheetLayout {
  const margins = options.margins ?? DEFAULT_MARGINS;
  const portrait = { widthIn: options.size.widthIn, heightIn: options.size.heightIn };
  const paper =
    options.orientation === "landscape"
      ? { widthIn: portrait.heightIn, heightIn: portrait.widthIn }
      : portrait;
  const drawing = {
    widthIn: Math.max(1, paper.widthIn - margins.edgeIn * 2),
    heightIn: Math.max(1, paper.heightIn - margins.edgeIn * 2 - margins.titleBlockIn),
  };

  const bounds = boundsOf(scene);
  // A margin of plan around the geometry so walls do not touch the border.
  const padding = 2 * PU_PER_FOOT;
  const padded = {
    min: { x: bounds.min.x - padding, y: bounds.min.y - padding },
    max: { x: bounds.max.x + padding, y: bounds.max.y + padding },
  };
  const worldWidth = Math.max(1, padded.max.x - padded.min.x);
  const worldHeight = Math.max(1, padded.max.y - padded.min.y);

  const requested = options.scale === "fit" ? null : options.scale;
  if (requested) {
    const requestedIndex = ARCHITECTURAL_SCALES.findIndex((s) => s.id === requested.id);
    const ladder = ARCHITECTURAL_SCALES.slice(Math.max(0, requestedIndex));
    for (const candidate of ladder) {
      const planWidth = paperInchesFor(worldWidth, candidate);
      const planHeight = paperInchesFor(worldHeight, candidate);
      if (planWidth <= drawing.widthIn && planHeight <= drawing.heightIn) {
        return {
          paper,
          drawing,
          bounds: padded,
          scale: candidate,
          scaleLabel: `Scale: ${candidate.label}`,
          plan: { widthIn: planWidth, heightIn: planHeight },
          scaleReduced: candidate.id !== requested.id,
        };
      }
    }
  }

  // Fitted: the drawing fills the sheet and says plainly that it is not to
  // scale, so nobody measures a door off it.
  const fit = Math.min(drawing.widthIn / worldWidth, drawing.heightIn / worldHeight);
  return {
    paper,
    drawing,
    bounds: padded,
    scale: null,
    scaleLabel: "NOT TO SCALE — fitted to the sheet",
    plan: { widthIn: worldWidth * fit, heightIn: worldHeight * fit },
    scaleReduced: options.scale !== "fit",
  };
}

/* ------------------------------------------------------------------ */
/* Printing the plan itself                                            */
/* ------------------------------------------------------------------ */

/** Ink for paper: black line work, greys for secondary information. */
const PRINT_STROKE: Partial<Record<StyleToken, string>> = {
  wall: "#000000",
  "wall-exterior": "#000000",
  "wall-selected": "#000000",
  "wall-hover": "#000000",
  boundary: "#777777",
  "opening-symbol": "#000000",
  "opening-selected": "#000000",
  object: "#333333",
  "object-selected": "#333333",
  "object-hover": "#333333",
  "object-warning": "#333333",
  "object-label": "#333333",
  label: "#444444",
  "label-strong": "#000000",
  "grid-minor": "#eeeeee",
  "grid-major": "#dddddd",
  /* Spec §83: annotations print black, like the drafting they are. */
  annotation: "#000000",
  dimension: "#000000",
  "dimension-unresolved": "#000000",
};

const PRINT_FILL: Partial<Record<StyleToken, string>> = {
  annotation: "#000000",
  dimension: "#000000",
  "dimension-unresolved": "#000000",
  region: "#fbfbfb",
  "region-unassigned": "#fbfbfb",
  "region-conflict": "#fbfbfb",
  "region-selected": "#fbfbfb",
  "region-hover": "#fbfbfb",
  // THE WALLS. These four were missing, and the polygon branch dropped any
  // style it did not recognise -- so every sheet printed rooms, labels and
  // dimensions with NO BUILDING around them. Found live; the regression
  // test below this file now fails if a scene with walls yields a sheet
  // without them.
  wall: "#3a3a3a",
  "wall-exterior": "#000000",
  "wall-selected": "#3a3a3a",
  "wall-hover": "#3a3a3a",
  "opening-gap": "#ffffff",
  "object-fill": "#f4f4f4",
  "object-counter": "#eeeeee",
};

/** Styles that are editing aids and never appear on paper. */
const SCREEN_ONLY = new Set<StyleToken>([
  "handle",
  "handle-pinned",
  "handle-moving",
  "snap",
  "guide",
  "gap",
  "anchor",
  "background",
  "grid-minor",
  "grid-major",
  "object-preview",
  "wall-preview",
  "boundary-preview",
  "region-hover",
  "region-selected",
]);

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * One primitive as SVG, in PLAN-UNIT coordinates with y flipped (plans are
 * drawn y-up; SVG is y-down). Line weights are given in plan units derived
 * from the paper scale, so a wall prints as a wall and not as a hairline.
 */
function primitiveToSvg(prim: ScenePrimitive, puPerPaperInch: number, annotationScale = 1): string {
  if (SCREEN_ONLY.has(prim.style)) return "";
  const w = (px: number) => (px * puPerPaperInch) / 96; // px at 96dpi → pu
  // Text legibility is a PAPER property (spec §70B): the multiplier applies
  // to annotation text and its layout offsets, never to geometry or line
  // weights, so a fitted plan keeps readable labels.
  const a = (px: number) => w(px) * annotationScale;
  const y = (value: number) => -value;

  if (prim.type === "polygon") {
    // A style this table has never heard of prints VISIBLY in grey rather
    // than vanishing. Dropping unknown geometry is how the walls went
    // missing: silence is the one failure mode a drawing must not have.
    const fill = PRINT_FILL[prim.style] ?? "#9a9a9a";
    const points = prim.points.map((p) => `${p.x},${y(p.y)}`).join(" ");
    return `<polygon points="${points}" fill="${fill}" stroke="none"/>`;
  }
  if (prim.type === "line") {
    const stroke = PRINT_STROKE[prim.style] ?? "#000000";
    const dash = prim.dash ? ` stroke-dasharray="${prim.dash.map((d) => w(d)).join(" ")}"` : "";
    return (
      `<line x1="${prim.from.x}" y1="${y(prim.from.y)}" x2="${prim.to.x}" y2="${y(prim.to.y)}" ` +
      `stroke="${stroke}" stroke-width="${Math.max(w(prim.widthPx), puPerPaperInch / 400)}"${dash}/>`
    );
  }
  if (prim.type === "arc") {
    const stroke = PRINT_STROKE[prim.style] ?? "#000000";
    const start = {
      x: prim.center.x + Math.cos(prim.startRad) * prim.radiusPu,
      y: prim.center.y + Math.sin(prim.startRad) * prim.radiusPu,
    };
    const end = {
      x: prim.center.x + Math.cos(prim.endRad) * prim.radiusPu,
      y: prim.center.y + Math.sin(prim.endRad) * prim.radiusPu,
    };
    // The scene always sweeps the short way; y is flipped, so the sweep flag
    // is inverted with it.
    const sweep = prim.ccw ? 0 : 1;
    return (
      `<path d="M ${start.x} ${y(start.y)} A ${prim.radiusPu} ${prim.radiusPu} 0 0 ${sweep} ${end.x} ${y(end.y)}" ` +
      `fill="none" stroke="${stroke}" stroke-width="${Math.max(w(prim.widthPx), puPerPaperInch / 400)}"/>`
    );
  }
  if (prim.type === "text") {
    const fill = PRINT_STROKE[prim.style] ?? "#000000";
    const size = a(prim.sizePx);
    const dx = prim.offsetPx ? a(prim.offsetPx[0]) : 0;
    const dy = prim.offsetPx ? a(prim.offsetPx[1]) : 0;
    const rotate = prim.angleRad ? ` transform="rotate(${(-prim.angleRad * 180) / Math.PI} ${prim.at.x + dx} ${y(prim.at.y) + dy})"` : "";
    return (
      `<text x="${prim.at.x + dx}" y="${y(prim.at.y) + dy}" font-size="${size}" fill="${fill}" ` +
      `text-anchor="middle" font-family="Helvetica, Arial, sans-serif"${rotate}>${escapeXml(prim.text)}</text>`
    );
  }
  return "";
}

/**
 * The whole sheet: plan, border and title block, as one self-contained SVG
 * sized in inches. Printed at 100% it is exactly to scale.
 *
 * NO Q CALLOUTS, NO LEADER LINES (spec §74B — permanent). The §68–§70
 * callout/leader machinery is gone: the drawing is a clean visual
 * reference, and the written bid scope identifies work by name and
 * location instead of by plan archaeology.
 */
export function designSheetSvg(
  scene: PlanScene,
  info: DesignSheetInfo,
  options: DesignSheetOptions,
): string {
  const layout = layoutDesignSheet(scene, options);
  const margins = options.margins ?? DEFAULT_MARGINS;
  const worldWidth = layout.bounds.max.x - layout.bounds.min.x;
  const puPerPaperInch = worldWidth / Math.max(layout.plan.widthIn, 0.0001);

  const annotationScale = options.annotationScale ?? 1;
  const body = [...scene.static]
    .map((prim) => primitiveToSvg(prim, puPerPaperInch, annotationScale))
    .filter((s) => s !== "")
    .join("\n      ");

  // Centre the plan inside the drawing area.
  const planX = margins.edgeIn + (layout.drawing.widthIn - layout.plan.widthIn) / 2;
  const planY = margins.edgeIn + (layout.drawing.heightIn - layout.plan.heightIn) / 2;
  const viewBox = `${layout.bounds.min.x} ${-layout.bounds.max.y} ${worldWidth} ${layout.bounds.max.y - layout.bounds.min.y}`;

  const titleTop = layout.paper.heightIn - margins.edgeIn - margins.titleBlockIn;
  const kindLabel = info.designKind === "existing" ? "EXISTING" : "PROPOSED";
  /*
   * ONE USER UNIT = ONE PAPER INCH, AND NOTHING INSIDE CARRIES A UNIT.
   *
   * The outer svg alone says "in": width/height give the sheet its physical
   * size, and the viewBox makes the user space numerically equal to inches.
   * Every child must then use PLAIN numbers. A child authored as x="0.5in"
   * is NOT half a user unit — SVG resolves "in" as a CSS inch, 96 user
   * units — so the entire drawing, border and title block landed ~96×
   * off-page and every sheet printed as a blank white rectangle. The
   * regression test parses the resolved coordinates back out and asserts
   * they sit inside the viewBox.
   */
  const line = (x1: number, y1: number, x2: number, y2: number, width = 0.008) =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#000" stroke-width="${width}"/>`;
  // Font sizes are given in points; 1pt = 1/72 inch = 1/72 user unit.
  const text = (x: number, y: number, value: string, size: number, weight = "normal", anchor = "start") =>
    `<text x="${x}" y="${y}" font-size="${size / 72}" font-weight="${weight}" text-anchor="${anchor}" ` +
    `font-family="Helvetica, Arial, sans-serif" fill="#000">${escapeXml(value)}</text>`;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${layout.paper.widthIn}in" height="${layout.paper.heightIn}in" ` +
      `viewBox="0 0 ${layout.paper.widthIn} ${layout.paper.heightIn}" class="design-sheet-svg">`,
    `  <rect x="0" y="0" width="${layout.paper.widthIn}" height="${layout.paper.heightIn}" fill="#ffffff"/>`,
    // The plan, in its own coordinate system, placed and sized in inches.
    `  <svg x="${planX}" y="${planY}" width="${layout.plan.widthIn}" height="${layout.plan.heightIn}" ` +
      `viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">`,
    `      ${body}`,
    `  </svg>`,
    // Border and title block.
    `  <g>`,
    `    <rect x="${margins.edgeIn}" y="${margins.edgeIn}" width="${layout.paper.widthIn - margins.edgeIn * 2}" ` +
      `height="${layout.paper.heightIn - margins.edgeIn * 2}" fill="none" stroke="#000" stroke-width="0.012"/>`,
    `    ${line(margins.edgeIn, titleTop, layout.paper.widthIn - margins.edgeIn, titleTop, 0.012)}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.32, fitSheetTitle(info.projectName,(layout.paper.widthIn-margins.edgeIn*2)/3-0.25,12), 12, "bold")}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.56, fitSheetTitle(info.address,(layout.paper.widthIn-margins.edgeIn*2)/3-0.25,9), 9)}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.8, `JOB ${info.jobNumber}`, 9)}`,
    `    ${text(layout.paper.widthIn / 2, titleTop + 0.32, fitSheetTitle(`${kindLabel} — ${info.designName}`,(layout.paper.widthIn-margins.edgeIn*2)/3-0.25,11), 11, "bold", "middle")}`,
    `    ${text(layout.paper.widthIn / 2, titleTop + 0.56, fitSheetTitle(info.floorName,(layout.paper.widthIn-margins.edgeIn*2)/3-0.25,9), 9, "normal", "middle")}`,
    `    ${text(layout.paper.widthIn / 2, titleTop + 0.8, layout.scaleLabel, 9, "normal", "middle")}`,
    `    ${text(layout.paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.32, fitSheetTitle(info.sheetTitle,(layout.paper.widthIn-margins.edgeIn*2)/3-0.25,11), 11, "bold", "end")}`,
    `    ${text(layout.paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.56, info.generatedAt.slice(0, 10), 9, "normal", "end")}`,
    `    ${text(layout.paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.8, info.sheetNumber, 12, "bold", "end")}`,
    `  </g>`,
    `</svg>`,
  ].join("\n");
}

/** A readable summary for the report header row. */
export function describeSheet(layout: DesignSheetLayout, size: SheetSize): string {
  const paper = `${layout.paper.widthIn} × ${layout.paper.heightIn} in`;
  return `${size.label} · ${paper} · ${layout.scaleLabel}`;
}

/** Feet of plan that fit across a sheet at a scale — used by the picker. */
export function widthInFeet(layout: DesignSheetLayout): number {
  return Math.round((layout.bounds.max.x - layout.bounds.min.x) / PU_PER_FOOT);
}

/** Exposed for the tests that prove a claimed scale is a real one. */
export function measuredPaperInches(pu: number, layout: DesignSheetLayout): number {
  if (!layout.scale) {
    const worldWidth = layout.bounds.max.x - layout.bounds.min.x;
    return (pu / worldWidth) * layout.plan.widthIn;
  }
  return paperInchesFor(pu, layout.scale);
}

/** Convenience for tests and the picker: a length in the sheet's own words. */
export function formatPlanLength(pu: number): string {
  return formatLength(Math.round(pu / PU_PER_INCH) * PU_PER_INCH);
}

/* ------------------------------------------------------------------ *
 * Multi-floor sheets (spec §69-1)
 * ------------------------------------------------------------------ */

export type FloorSheetLayoutMode = "auto" | "per-floor" | "fit";

/**
 * AUTO ORIENTATION (spec §70F). Deterministic: evaluate both orientations
 * for the floors being printed and pick the better —
 *   exact scale: the orientation where no floor steps down the ladder wins;
 *     then fewer sheets; then the larger fitted-plan area;
 *   fit: the larger total plan area wins; then fewer sheets.
 * Ties break to landscape, the way a plan set is usually bound. The same
 * floors and options always choose the same orientation.
 */
export function resolveOrientation(
  entries: readonly FloorSheetEntry[],
  options: Omit<DesignSheetOptions, "orientation">,
  requested: "auto" | "portrait" | "landscape",
  layoutMode: FloorSheetLayoutMode,
): "portrait" | "landscape" {
  if (requested !== "auto") return requested;
  if (entries.length === 0) return "landscape";
  const scaleIndex = (id: string | null): number =>
    id === null ? ARCHITECTURAL_SCALES.length : ARCHITECTURAL_SCALES.findIndex((s) => s.id === id);
  const evaluate = (orientation: "portrait" | "landscape") => {
    const full: DesignSheetOptions = { ...options, orientation };
    const sheets = planFloorSheets(entries, full, options.scale === "fit" ? "fit" : layoutMode);
    let reduced = 0;
    let worstScale = -1;
    let area = 0;
    for (const entry of entries) {
      const layout = layoutDesignSheet(entry.scene, full);
      if (layout.scaleReduced) reduced += 1;
      worstScale = Math.max(worstScale, scaleIndex(layout.scale?.id ?? null));
      area += layout.plan.widthIn * layout.plan.heightIn;
    }
    return { sheets: sheets.length, reduced, worstScale, area };
  };
  const landscape = evaluate("landscape");
  const portrait = evaluate("portrait");
  if (options.scale !== "fit") {
    if (landscape.reduced !== portrait.reduced) return landscape.reduced < portrait.reduced ? "landscape" : "portrait";
    if (landscape.worstScale !== portrait.worstScale) return landscape.worstScale < portrait.worstScale ? "landscape" : "portrait";
  }
  if (landscape.sheets !== portrait.sheets) return landscape.sheets < portrait.sheets ? "landscape" : "portrait";
  if (Math.abs(landscape.area - portrait.area) > 0.01) return landscape.area > portrait.area ? "landscape" : "portrait";
  return "landscape";
}

export interface FloorSheetEntry {
  floorId: string;
  floorName: string;
  scene: PlanScene;
}

export interface PlannedSheet {
  /** The floors on this physical sheet, in order. */
  floors: FloorSheetEntry[];
  /** "A-1.0", "A-1.1", … */
  sheetNumber: string;
}

/**
 * WHICH FLOORS SHARE A SHEET (spec §69-1).
 *
 * The rule is SAME SCALE FIRST. Floors may share one page only when every
 * one of them fits on that page at the SAME exact architectural scale, side
 * by side with a gutter. Nothing is ever shrunk to a different scale to
 * force it on: a sheet where Floor 1 is quarter-inch and Floor 2 is
 * three-sixteenths is a drawing that lies twice.
 *
 * "fit" mode never shares a page, because each fitted floor has its own
 * (non-)scale and putting two on one sheet would invite comparing them.
 *
 * Pure and deterministic: the same design always paginates the same way.
 */
export function planFloorSheets(
  entries: readonly FloorSheetEntry[],
  options: DesignSheetOptions,
  mode: FloorSheetLayoutMode,
): PlannedSheet[] {
  const number = (index: number) => `A-1.${index}`;
  const separate = (): PlannedSheet[] =>
    entries.map((entry, index) => ({ floors: [entry], sheetNumber: number(index) }));

  if (mode === "per-floor" || mode === "fit" || options.scale === "fit" || entries.length <= 1) {
    return separate();
  }

  // Exact scale: do ALL floors fit on one sheet at the REQUESTED scale?
  const margins = options.margins ?? DEFAULT_MARGINS;
  const paper =
    options.orientation === "landscape"
      ? { widthIn: options.size.heightIn, heightIn: options.size.widthIn }
      : { widthIn: options.size.widthIn, heightIn: options.size.heightIn };
  const drawing = {
    widthIn: Math.max(1, paper.widthIn - margins.edgeIn * 2),
    heightIn: Math.max(1, paper.heightIn - margins.edgeIn * 2 - margins.titleBlockIn),
  };
  const gutterIn = 0.4;
  const padding = 2 * PU_PER_FOOT;

  const scale = options.scale;
  let totalWidth = 0;
  let tallest = 0;
  for (const entry of entries) {
    const bounds = boundsOf(entry.scene);
    const widthIn = paperInchesFor(bounds.max.x - bounds.min.x + padding * 2, scale);
    const heightIn = paperInchesFor(bounds.max.y - bounds.min.y + padding * 2, scale);
    totalWidth += widthIn;
    tallest = Math.max(tallest, heightIn);
  }
  totalWidth += gutterIn * (entries.length - 1);
  // Headroom for the per-floor caption under each plan.
  const captionIn = 0.3;
  if (totalWidth <= drawing.widthIn && tallest + captionIn <= drawing.heightIn) {
    return [{ floors: [...entries], sheetNumber: "A-1.0" }];
  }
  return separate();
}

/**
 * Several floors on ONE sheet, side by side, all at the same scale. Each
 * floor keeps its own inner viewport and gets its name captioned beneath
 * it; the title block states the one scale everything on the page shares.
 */
export function multiFloorSheetSvg(
  floors: readonly FloorSheetEntry[],
  info: DesignSheetInfo,
  options: DesignSheetOptions,
): string {
  if (floors.length === 1) {
    return designSheetSvg(floors[0].scene, { ...info, floorName: floors[0].floorName }, options);
  }
  const margins = options.margins ?? DEFAULT_MARGINS;
  const paper =
    options.orientation === "landscape"
      ? { widthIn: options.size.heightIn, heightIn: options.size.widthIn }
      : { widthIn: options.size.widthIn, heightIn: options.size.heightIn };
  const drawingArea = {
    widthIn: Math.max(1, paper.widthIn - margins.edgeIn * 2),
    heightIn: Math.max(1, paper.heightIn - margins.edgeIn * 2 - margins.titleBlockIn),
  };
  const scale = options.scale === "fit" ? ARCHITECTURAL_SCALES[1] : options.scale;
  const gutterIn = 0.4;
  const padding = 2 * PU_PER_FOOT;

  // Unitless user units — see the coordinate-unit rule in designSheetSvg.
  const line = (x1: number, y1: number, x2: number, y2: number, width = 0.008) =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#000" stroke-width="${width}"/>`;
  const text = (x: number, y: number, value: string, size: number, weight = "normal", anchor = "start") =>
    `<text x="${x}" y="${y}" font-size="${size / 72}" font-weight="${weight}" text-anchor="${anchor}" ` +
    `font-family="Helvetica, Arial, sans-serif" fill="#000">${escapeXml(value)}</text>`;

  const plans = floors.map((entry) => {
    const bounds = boundsOf(entry.scene);
    const padded = {
      min: { x: bounds.min.x - padding, y: bounds.min.y - padding },
      max: { x: bounds.max.x + padding, y: bounds.max.y + padding },
    };
    return {
      entry,
      padded,
      widthIn: paperInchesFor(padded.max.x - padded.min.x, scale),
      heightIn: paperInchesFor(padded.max.y - padded.min.y, scale),
    };
  });
  const rowWidth = plans.reduce((sum, p) => sum + p.widthIn, 0) + gutterIn * (plans.length - 1);
  let cursorX = margins.edgeIn + Math.max(0, (drawingArea.widthIn - rowWidth) / 2);
  const rowTop =
    margins.edgeIn + Math.max(0, (drawingArea.heightIn - 0.3 - Math.max(...plans.map((p) => p.heightIn))) / 2);

  const bodies: string[] = [];
  for (const plan of plans) {
    const worldWidth = plan.padded.max.x - plan.padded.min.x;
    const puPerPaperInch = worldWidth / Math.max(plan.widthIn, 0.0001);
    const body = [...plan.entry.scene.static]
      .map((prim) => primitiveToSvg(prim, puPerPaperInch, options.annotationScale ?? 1))
      .filter((s) => s !== "")
      .join("\n      ");
    const viewBox = `${plan.padded.min.x} ${-plan.padded.max.y} ${worldWidth} ${plan.padded.max.y - plan.padded.min.y}`;
    bodies.push(
      `  <svg x="${cursorX}" y="${rowTop}" width="${plan.widthIn}" height="${plan.heightIn}" ` +
        `viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">\n      ${body}\n  </svg>`,
      `  ${text(cursorX + plan.widthIn / 2, rowTop + plan.heightIn + 0.25, plan.entry.floorName.toUpperCase(), 9, "bold", "middle")}`,
    );
    cursorX += plan.widthIn + gutterIn;
  }

  const titleTop = paper.heightIn - margins.edgeIn - margins.titleBlockIn;
  const kindLabel = info.designKind === "existing" ? "EXISTING" : "PROPOSED";
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${paper.widthIn}in" height="${paper.heightIn}in" ` +
      `viewBox="0 0 ${paper.widthIn} ${paper.heightIn}" class="design-sheet-svg">`,
    `  <rect x="0" y="0" width="${paper.widthIn}" height="${paper.heightIn}" fill="#ffffff"/>`,
    bodies.join("\n"),
    `  <g>`,
    `    <rect x="${margins.edgeIn}" y="${margins.edgeIn}" width="${paper.widthIn - margins.edgeIn * 2}" ` +
      `height="${paper.heightIn - margins.edgeIn * 2}" fill="none" stroke="#000" stroke-width="0.012"/>`,
    `    ${line(margins.edgeIn, titleTop, paper.widthIn - margins.edgeIn, titleTop, 0.012)}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.32, fitSheetTitle(info.projectName,(paper.widthIn-margins.edgeIn*2)/3-0.25,12), 12, "bold")}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.56, fitSheetTitle(info.address,(paper.widthIn-margins.edgeIn*2)/3-0.25,9), 9)}`,
    `    ${text(margins.edgeIn + 0.15, titleTop + 0.8, `JOB ${info.jobNumber}`, 9)}`,
    `    ${text(paper.widthIn / 2, titleTop + 0.32, fitSheetTitle(`${kindLabel} — ${info.designName}`,(paper.widthIn-margins.edgeIn*2)/3-0.25,11), 11, "bold", "middle")}`,
    `    ${text(paper.widthIn / 2, titleTop + 0.56, fitSheetTitle(floors.map((f) => f.floorName).join(" · "),(paper.widthIn-margins.edgeIn*2)/3-0.25,9), 9, "normal", "middle")}`,
    `    ${text(paper.widthIn / 2, titleTop + 0.8, `Scale: ${scale.label}`, 9, "normal", "middle")}`,
    `    ${text(paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.32, fitSheetTitle(info.sheetTitle,(paper.widthIn-margins.edgeIn*2)/3-0.25,11), 11, "bold", "end")}`,
    `    ${text(paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.56, info.generatedAt.slice(0, 10), 9, "normal", "end")}`,
    `    ${text(paper.widthIn - margins.edgeIn - 0.15, titleTop + 0.8, info.sheetNumber, 12, "bold", "end")}`,
    `  </g>`,
    `</svg>`,
  ].join("\n");
}
